import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import { PackageType } from "@b2bportal/core-packages";
import { ProductType } from "@b2bportal/core-types";
import { getPackageRoutes } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ErrorModal } from "@tenant/ui";
import { PackageBaseSelectors } from "@/store/packages/selectors";

interface PackageNoAvailabilityModalProps {
  shouldShow: boolean;
}

/**
 * Maps package type to its first step pathname
 */
const getFirstStepPath = (packageType: PackageType | null): string | null => {
  const { FULL_ROUTES } = getPackageRoutes();

  switch (packageType) {
    case PackageType.FLIGHT_CAR:
      return FULL_ROUTES.FLIGHTS.DEPARTURE;
    case PackageType.HOTEL_CAR:
    case PackageType.HOTEL_FLIGHT_CAR:
      return FULL_ROUTES.HOTELS.AVAILABILITY;
    default:
      return null;
  }
};

export const PackageNoAvailabilityModal = ({
  shouldShow,
}: PackageNoAvailabilityModalProps) => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const packageType = useSelector(
    PackageBaseSelectors.getCurrentActivePackageType
  );

  // Determine if we're on the first step of the package flow
  const isFirstStep = (() => {
    const firstStepPath = getFirstStepPath(packageType);
    if (!firstStepPath) return false;
    return location.pathname.startsWith(firstStepPath);
  })();

  // Show modal when shouldShow is true, not loading, and NOT on first step
  useEffect(() => {
    if (shouldShow && !isFirstStep) {
      setIsModalOpen(true);
    }
  }, [shouldShow, isFirstStep]);

  const handleStartNewSearch = useCallback(() => {
    setIsModalOpen(false);
    // Navigate to packages homepage (search page)
    const { BASE_ROUTE } = getPackageRoutes();
    navigate(BASE_ROUTE);
  }, [navigate]);

  return (
    <ErrorModal
      open={isModalOpen}
      title={t("core-packages.noAvailability.title")}
      subtitle={t("core-packages.noAvailability.description")}
      primaryButtonText={t("core-packages.noAvailability.button")}
      primaryOnClick={handleStartNewSearch}
      productType={ProductType.PACKAGE}
      onClose={() => setIsModalOpen(false)}
    />
  );
};
